/** Private reads are scoped to both a request-local customer store and Nuxt app. */
export interface UserReadIdentity {
  token: string;
  store: string;
  locale: string;
  currency: string;
}

export interface UserReadToken {
  epoch: number;
  sequence: number;
  identity: UserReadIdentity;
}

interface UserReadScope {
  epoch: number;
  sequence: number;
  pendingWrites: number;
  inFlight?: {
    token: UserReadToken;
    contract: string;
    promise: Promise<unknown>;
  };
  snapshot?: {
    token: UserReadToken;
    contract: string;
    value: unknown;
  };
}

const scopes = new WeakMap<object, WeakMap<object, UserReadScope>>();

export function getUserReadScope(store: object, app: object): UserReadScope {
  let applications = scopes.get(store);
  if (!applications) {
    applications = new WeakMap();
    scopes.set(store, applications);
  }
  let scope = applications.get(app);
  if (!scope) {
    scope = { epoch: 0, sequence: 0, pendingWrites: 0 };
    applications.set(app, scope);
  }
  return scope;
}

export function sameUserReadIdentity(first: UserReadIdentity, second: UserReadIdentity): boolean {
  return first.token === second.token && first.store === second.store
    && first.locale === second.locale && first.currency === second.currency;
}

export function invalidateUserReads(scope: UserReadScope): void {
  scope.epoch += 1;
  scope.sequence += 1;
  scope.snapshot = undefined;
  scope.inFlight = undefined;
}

/** Profile/address writes invalidate before and after, including rejected writes. */
export function beginUserWrite(scope: UserReadScope): () => void {
  invalidateUserReads(scope);
  scope.pendingWrites += 1;
  let completed = false;
  return () => {
    if (completed) return;
    completed = true;
    scope.pendingWrites -= 1;
    invalidateUserReads(scope);
  };
}

export function userReadContractKey(customQuery: unknown, customHeaders: unknown): string | null {
  const normalize = (value: unknown): unknown => {
    if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (Array.isArray(value)) return Array.from(value, normalize);
    if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
      return Object.fromEntries(Object.keys(value).sort().map((key) => [key, normalize((value as Record<string, unknown>)[key])]));
    }
    throw new Error('Unsupported private user read contract');
  };
  try {
    return JSON.stringify(normalize({ query: customQuery ?? {}, headers: customHeaders ?? {} }));
  } catch {
    return null;
  }
}

export function canApplyUserRead(scope: UserReadScope, read: UserReadToken, current: UserReadIdentity): boolean {
  return scope.epoch === read.epoch && scope.sequence === read.sequence
    && scope.pendingWrites === 0 && sameUserReadIdentity(read.identity, current);
}

/** The real default customer query has no root id. Confirm its address contract, not an id sentinel. */
export function hasCompleteUserAddresses(value: any): boolean {
  const present = (object: any, fields: string[]) => object && typeof object === 'object'
    && fields.every((field) => Object.prototype.hasOwnProperty.call(object, field) && object[field] !== undefined);
  if (!present(value, ['email', 'firstname', 'lastname', 'default_shipping', 'default_billing', 'addresses'])
    || !Array.isArray(value.addresses)) return false;
  return value.addresses.every((address: any) => present(address, [
    'id', 'firstname', 'lastname', 'city', 'country_code', 'street', 'region', 'telephone', 'postcode',
    'default_billing', 'default_shipping',
  ]) && Number.isInteger(address.id) && address.id > 0 && Array.isArray(address.street)
    && (address.region === null || present(address.region, ['region', 'region_id', 'region_code'])));
}

export function hasCurrentUserSnapshot(
  scope: UserReadScope,
  identity: UserReadIdentity,
  contract: string | null,
  value: unknown,
): boolean {
  const { snapshot } = scope;
  return Boolean(contract && snapshot && snapshot.contract === contract && snapshot.value === value
    && canApplyUserRead(scope, snapshot.token, identity) && hasCompleteUserAddresses(value));
}

export function rememberUserReadSnapshot(
  scope: UserReadScope,
  read: UserReadToken,
  identity: UserReadIdentity,
  contract: string | null,
  value: unknown,
): void {
  if (!contract || !canApplyUserRead(scope, read, identity) || !hasCompleteUserAddresses(value)) return;
  scope.snapshot = { token: read, contract, value };
}

/** Only a pending physical request is shared; ordinary loads never use settled data. */
export function shareUserRead<T>(
  scope: UserReadScope,
  identity: UserReadIdentity,
  contract: string | null,
  force: boolean,
  execute: (read: UserReadToken) => Promise<T>,
): Promise<T> {
  const active = scope.inFlight;
  if (!force && contract && active && active.contract === contract
    && scope.epoch === active.token.epoch && scope.sequence === active.token.sequence
    && sameUserReadIdentity(active.token.identity, identity)) return active.promise as Promise<T>;
  scope.sequence += 1;
  scope.snapshot = undefined;
  scope.inFlight = undefined;
  const read = { epoch: scope.epoch, sequence: scope.sequence, identity: { ...identity } };
  const promise = execute(read);
  if (contract) {
    const entry = { token: read, contract, promise };
    scope.inFlight = entry;
    const clear = () => { if (scope.inFlight === entry) scope.inFlight = undefined; };
    promise.then(clear).catch(clear);
  }
  return promise;
}
